import { useEffect, useState } from "react";
import { Mic, Play, Heart, Sliders, PlugZap, Volume2 } from "lucide-react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner, cx } from "@/components/shared";

function Slider({ label, value, onChange, min = 0, max = 100 }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-400">{label}</span><span className="font-mono text-[#FFB300]">{value}</span></div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`voice-slider-${label.toLowerCase()}`}
        className="w-full accent-[#5B4DFF] h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer" />
    </div>
  );
}

export default function VoiceStudio() {
  const [catalogs, setCatalogs] = useState(null);
  const [providers, setProviders] = useState(null);
  const [voice, setVoice] = useState("adult_male");
  const [style, setStyle] = useState("Storytelling");
  const [language, setLanguage] = useState("English");
  const [text, setText] = useState("Ideas become films — Vyom brings your story to life.");
  const [controls, setControls] = useState({ Emotion: 50, Pitch: 50, Speed: 50, Pause: 30 });
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    api.get("/catalogs").then(({ data }) => setCatalogs(data)).catch(() => {});
    api.get("/providers").then(({ data }) => setProviders(data)).catch(() => {});
  }, []);

  const [previewing, setPreviewing] = useState(false);

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/voices/preview", { text, voice, controls: { ...controls, style } });
      if (data.url) {
        const audio = new Audio(mediaUrl(data.url));
        audio.play();
        toast.success("Playing preview");
      } else {
        toast.info(data.message || "Connect a voice provider to synthesize audio.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreviewing(false); }
  };

  const toggleFav = (id) => setFavorites((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]);

  if (!catalogs) return <Spinner label="Loading Voice Studio…" />;
  const configured = providers?.voice?.configured;

  return (
    <CinematicStage>
      <StageHeader stage="11" room="The Voice Booth · Voice Studio" icon={Mic} title="Design the perfect narration"
        subtitle="Human voice archetypes, narration styles and fine controls — provider-ready."
        actions={<Pill tone={configured ? "green" : "slate"}><PlugZap className="w-3 h-3" /> {configured ? "Provider connected" : "Preview mode"}</Pill>} />

      {!configured && (
        <GlassCard className="p-4 mb-6 flex items-center gap-3 border-[#FFB300]/25">
          <Volume2 className="w-5 h-5 text-[#FFB300]" />
          <span className="text-sm text-slate-300">Connect a voice provider (ElevenLabs / OpenAI TTS) in Settings to synthesize real audio. Everything else is ready.</span>
        </GlassCard>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <GlassCard className="p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Mic className="w-4 h-4 text-[#5B4DFF]" /> Human Voices</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {catalogs.voices.map((v) => (
                <button key={v.id} onClick={() => setVoice(v.id)} data-testid={`voice-select-${v.id}`}
                  className={cx("flex items-center gap-3 p-3 rounded-xl border text-left transition",
                    voice === v.id ? "bg-[#5B4DFF]/20 border-[#5B4DFF]/50" : "bg-white/5 border-white/10 hover:border-[#5B4DFF]/30")}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center font-semibold">{v.name[0]}</div>
                  <div className="flex-1"><div className="text-sm text-white">{v.name}</div><div className="text-xs text-slate-500">{v.type}</div></div>
                  <Heart onClick={(e) => { e.stopPropagation(); toggleFav(v.id); }} className={cx("w-4 h-4", favorites.includes(v.id) ? "fill-[#FFB300] text-[#FFB300]" : "text-slate-500")} />
                </button>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="font-display font-semibold mb-4">Narration Style</h3>
            <div className="flex flex-wrap gap-2">
              {catalogs.narration_styles.map((s) => (
                <button key={s} onClick={() => setStyle(s)} data-testid={`narration-${s}`}
                  className={cx("px-4 py-2 rounded-xl text-sm border transition", style === s ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}>{s}</button>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="font-display font-semibold mb-3">Preview Text</h3>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="voice-text-input"
              className="w-full bg-white/5 border border-white/10 focus:border-[#5B4DFF]/60 rounded-xl p-3 outline-none text-white text-sm resize-none" />
            <div className="mt-3 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center gap-1 px-4 overflow-hidden">
              {Array.from({ length: 48 }).map((_, i) => <div key={i} className="w-1 rounded-full bg-[#5B4DFF]/50" style={{ height: `${20 + Math.abs(Math.sin(i * 0.7)) * 60}%` }} />)}
            </div>
            <button onClick={preview} disabled={previewing} data-testid="voice-preview-button" className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5B4DFF] text-white font-semibold glow-purple disabled:opacity-60">
              <Play className="w-4 h-4" /> {previewing ? "Generating…" : "Preview Voice"}
            </button>
          </GlassCard>
        </div>

        <div className="space-y-6">
          <GlassCard className="p-6">
            <h3 className="font-display font-semibold mb-5 flex items-center gap-2"><Sliders className="w-4 h-4 text-[#FFB300]" /> Controls</h3>
            <div className="space-y-5">
              {Object.entries(controls).map(([k, v]) => <Slider key={k} label={k} value={v} onChange={(nv) => setControls((c) => ({ ...c, [k]: nv }))} />)}
            </div>
          </GlassCard>
          <GlassCard className="p-6">
            <h3 className="font-display font-semibold mb-3">Language</h3>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="voice-language-select"
              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 outline-none text-white text-sm">
              {catalogs.languages.map((l) => <option key={l} value={l} className="bg-[#0A1435]">{l}</option>)}
            </select>
          </GlassCard>
        </div>
      </div>
    </CinematicStage>
  );
}
